// The Console's state: one small store, read with hooks. Pages read it and
// call actions (actions.ts); they don't fetch. Nothing is persisted: the
// Console never stores a token, and AuthKit keeps its own session (see ui/src/lib/auth.ts).

import { useEffect, useReducer, useRef } from "preact/hooks";
import type { UseCase } from "../domain/examples.ts";
import type { Lesson, LessonState } from "../domain/lesson.ts";
import { EMPTY_REQUEST_TEXT } from "../domain/request.ts";
import type { ShapeCache } from "../domain/shapes.ts";
import type { AccountResponse, ApiFailure, AskResponse } from "../domain/types.ts";
import { SAMPLE, type Sample } from "../domain/sample.ts";
import type { AuthkitConfig } from "../../../ui/src/index.ts";

/** `authkit` is null where sign-in isn't configured; there, no one can sign in, because every Consumer API request needs a token. */
/** `studioUrl`: Curator Studio in the same environment, for the wordmark's app switcher. */
export type Config = { env: string; label: string; apiBase: string; studioUrl: string; authkit: AuthkitConfig | null };
export type { Sample, UseCase };
/** Lessons and use cases are built from GET /v1/examples once the consumer is signed in; the sample data is the Console's own. */
export type Content = { lessons: Lesson[]; useCases: UseCase[]; sample: Sample };
export type ExamplesStatus = "idle" | "loading" | "ready" | "failed";

/** A charge or a refund that happened in this session, from a real response. */
export type LedgerEntry = { type: "Charge" | "Refund"; what: string; hub: string; amount: number; at: number; answerId: string };

export type ResponseState =
  | { kind: "ok"; value: AskResponse; at: number }
  | { kind: "error"; error: ApiFailure };

export type ResponseView = "answer" | "table" | "flow" | "json";

export type ConsoleState = {
  config: Config;
  content: Content;
  examples: ExamplesStatus;
  signedIn: boolean;
  /** The signed-in WarmHub user, from AuthKit and POST /v1/session; null until the session starts. */
  user: { email: string | null; account: string } | null;
  /** Why the Exchange ended the session, such as an expired token or sign-up that isn't open, for the sign-in page; null otherwise. */
  signedOutReason: string | null;
  /** The Exchange refused the account, so signing in again as it can't help; see isRefusedSession. */
  signedOutRefused: boolean;
  /** US dollars, from the last response; null when unknown. */
  balance: number | null;
  ledger: LedgerEntry[];
  /** The consumer account from GET /v1/account, once loaded; null before, or when it couldn't be read. */
  account: AccountResponse | null;
  requestText: string;
  requestView: "json" | "form";
  sending: boolean;
  response: ResponseState | null;
  /** The request changed after this response came back. */
  stale: boolean;
  view: ResponseView;
  lesson: LessonState | null;
  creditsOpen: boolean;
  rejecting: { answerId: string; hub: string; price: number } | null;
  refunded: string[];
  /** Thing titles by wref, from GET /v1/things. */
  titles: Record<string, string>;
  /** Shape definitions by pinned shape ref, from GET /v1/shapes. */
  shapes: ShapeCache;
  notice: string | null;
};

export const initialState = (config: Config, signedIn = false): ConsoleState => ({
  config, content: { lessons: [], useCases: [], sample: SAMPLE }, examples: "idle", signedIn, user: null, signedOutReason: null, signedOutRefused: false, balance: null, ledger: [], account: null,
  requestText: EMPTY_REQUEST_TEXT, requestView: "json", sending: false, response: null, stale: false, view: "answer",
  lesson: null, creditsOpen: false, rejecting: null, refunded: [], titles: {}, shapes: {}, notice: null,
});

let state: ConsoleState;
const listeners = new Set<() => void>();

export const getState = (): ConsoleState => state;
export function initStore(s: ConsoleState) { state = s; }
export function setState(change: Partial<ConsoleState> | ((s: ConsoleState) => Partial<ConsoleState>)) {
  state = { ...state, ...(typeof change === "function" ? change(state) : change) };
  listeners.forEach((l) => l());
}

/** Reads part of the state with this render's selector; the component re-renders when that part changes. */
export function useStore<T>(select: (s: ConsoleState) => T): T {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const value = select(state);
  // The subscription outlives this render, so it reads the latest selector and
  // value through refs: a selector that depends on props or the route stays current.
  const latest = useRef({ select, value });
  latest.current = { select, value };
  useEffect(() => {
    const update = () => { if (!Object.is(latest.current.select(state), latest.current.value)) rerender(0); };
    listeners.add(update);
    update();
    return () => { listeners.delete(update); };
  }, []);
  return value;
}
